import piexif from "piexifjs";

export interface ExifGpsData {
  latitude: number;
  longitude: number;
  timestamp: Date;
}

/**
 * Check if we're in fake location mode (no EXIF injection)
 */
function isFakeLocationMode(): boolean {
  return !!import.meta.env.VITE_FAKE_LOCATION;
}

/**
 * Convert decimal degrees to degrees/minutes/seconds format required by EXIF
 */
function decimalToDMS(
  decimal: number,
): [[number, number], [number, number], [number, number]] {
  const absolute = Math.abs(decimal);
  const degrees = Math.floor(absolute);
  const minutesFloat = (absolute - degrees) * 60;
  const minutes = Math.floor(minutesFloat);
  const seconds = Math.round((minutesFloat - minutes) * 60 * 100); // multiply by 100 for precision

  return [
    [degrees, 1],
    [minutes, 1],
    [seconds, 100],
  ];
}

/**
 * Inject GPS coordinates and timestamp into a JPEG image
 * Returns the modified image as a data URL
 *
 * If VITE_FAKE_LOCATION is set, returns the original image unchanged
 */
export function injectExifData(
  imageDataUrl: string,
  latitude: number,
  longitude: number,
  timestamp: Date = new Date(),
): string {
  // Skip EXIF injection in fake location mode
  if (isFakeLocationMode()) {
    return imageDataUrl;
  }

  // Create EXIF data object
  const exifObj: Record<string, Record<string, unknown>> = {
    "0th": {},
    Exif: {},
    GPS: {},
    "1st": {},
  };

  // Set GPS data
  exifObj.GPS[piexif.GPSIFD.GPSLatitudeRef] = latitude >= 0 ? "N" : "S";
  exifObj.GPS[piexif.GPSIFD.GPSLatitude] = decimalToDMS(latitude);
  exifObj.GPS[piexif.GPSIFD.GPSLongitudeRef] = longitude >= 0 ? "E" : "W";
  exifObj.GPS[piexif.GPSIFD.GPSLongitude] = decimalToDMS(longitude);

  // Set timestamp
  const dateTimeStr = formatExifDateTime(timestamp);
  exifObj.Exif[piexif.ExifIFD.DateTimeOriginal] = dateTimeStr;
  exifObj.Exif[piexif.ExifIFD.DateTimeDigitized] = dateTimeStr;
  exifObj["0th"][piexif.ImageIFD.DateTime] = dateTimeStr;

  // Convert EXIF object to binary string
  const exifBytes = piexif.dump(exifObj);

  // Insert EXIF into image
  const newImageDataUrl = piexif.insert(exifBytes, imageDataUrl);

  return newImageDataUrl;
}

/**
 * Format a Date object as EXIF datetime string (YYYY:MM:DD HH:MM:SS)
 */
function formatExifDateTime(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${year}:${month}:${day} ${hours}:${minutes}:${seconds}`;
}

/**
 * Parse EXIF datetime string to Date object
 */
function parseExifDateTime(exifDateTime: string): Date | null {
  // EXIF format: "YYYY:MM:DD HH:MM:SS"
  const match = exifDateTime.match(
    /^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})$/,
  );
  if (!match) return null;

  const [, year, month, day, hours, minutes, seconds] = match;
  return new Date(
    parseInt(year),
    parseInt(month) - 1,
    parseInt(day),
    parseInt(hours),
    parseInt(minutes),
    parseInt(seconds),
  );
}

/**
 * Convert DMS (degrees/minutes/seconds) array to decimal degrees
 */
function dmsToDecimal(
  dms: [[number, number], [number, number], [number, number]],
  ref: string,
): number {
  const degrees = dms[0][0] / dms[0][1];
  const minutes = dms[1][0] / dms[1][1];
  const seconds = dms[2][0] / dms[2][1];

  let decimal = degrees + minutes / 60 + seconds / 3600;

  if (ref === "S" || ref === "W") {
    decimal = -decimal;
  }

  return decimal;
}

/**
 * Read EXIF GPS and timestamp data from a JPEG image
 * Returns null if no EXIF data found
 */
export function readExifData(imageDataUrl: string): ExifGpsData | null {
  try {
    const exifObj = piexif.load(imageDataUrl);

    const gps = exifObj.GPS;
    const exif = exifObj.Exif;

    if (!gps || !exif) {
      return null;
    }

    // Read GPS coordinates
    const latRef = gps[piexif.GPSIFD.GPSLatitudeRef];
    const lat = gps[piexif.GPSIFD.GPSLatitude];
    const lonRef = gps[piexif.GPSIFD.GPSLongitudeRef];
    const lon = gps[piexif.GPSIFD.GPSLongitude];

    if (!lat || !lon || !latRef || !lonRef) {
      return null;
    }

    const latitude = dmsToDecimal(
      lat as [[number, number], [number, number], [number, number]],
      latRef as string,
    );
    const longitude = dmsToDecimal(
      lon as [[number, number], [number, number], [number, number]],
      lonRef as string,
    );

    // Read timestamp
    const dateTimeOriginal = exif[piexif.ExifIFD.DateTimeOriginal] as
      | string
      | undefined;
    const timestamp = dateTimeOriginal
      ? parseExifDateTime(dateTimeOriginal)
      : null;

    if (!timestamp) {
      return null;
    }

    return {
      latitude,
      longitude,
      timestamp,
    };
  } catch (error) {
    console.error("Error reading EXIF data:", error);
    return null;
  }
}

/**
 * Read EXIF data from an image URL (fetches the image first)
 */
export async function readExifDataFromUrl(
  imageUrl: string,
): Promise<ExifGpsData | null> {
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        resolve(readExifData(dataUrl));
      };
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error("Error fetching image for EXIF reading:", error);
    return null;
  }
}
